/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ProactiveChatNames } from '../../helpers/constants';
import { IExtendedRequest } from '../../interfaces';
import { IChatContext, ISpecificLocationTriggerContext, TContextProvider } from '../../../interfaces';
import { IMsdyn365CsChatConnectorViewProps } from '../../msdyn365-cs-chat-connector';
import ProactiveChatTrigger from './proactive-chat-trigger';

/**
 * SpecificPageTrigger class.
 * Proactive chat is triggered if user geographically belongs to the list of country codes: (config.proactiveOnSpecificCountryRegionCountry).
 */
export default class SpecificCountryRegionTrigger extends ProactiveChatTrigger {
    private customerCountryCode: string;

    /**
     * Class constructor.
     * @param {IMsdyn365CsChatConnectorViewProps} props The chat connector view properties.
     * @param {IChatContext} context The context.
     */
    constructor(props: IMsdyn365CsChatConnectorViewProps, context: IChatContext) {
        super(props, context);
        this.customerCountryCode = (<IExtendedRequest>this.props.context.request).suggestedMarket || 'n/a';
        this.props.context.telemetry.debug(
            `[${SpecificCountryRegionTrigger.name}] Customer country code set to: '${this.customerCountryCode}.'`
        );
    }

    /**
     * Validates whether the condition(s) is met to trigger proactive chat: user geographically belongs to the list of country codes.
     * @returns {Promise<boolean>} True if condition is met. Otherwise, false.
     */
    public async validate(): Promise<boolean> {
        const retVal: boolean = this.props.config.proactiveOnSpecificCountryRegionCountry
            ? this.props.config.proactiveOnSpecificCountryRegionCountry.toLowerCase().indexOf(this.customerCountryCode.toLowerCase()) > -1
            : false;

        this.props.context.telemetry.debug(`[Chat][${SpecificCountryRegionTrigger.name}] Validation returning '${retVal}'.`);
        return retVal;
    }

    /**
     * Gets the context.
     * @returns {TContextProvider} Chat context.
     */
    public getContextProvider(): TContextProvider {
        return (): ISpecificLocationTriggerContext => {
            return {
                ...this.context,
                IsProactiveChat: {
                    value: 'True',
                    isDisplayable: true
                },
                CustCountryCode: {
                    value: this.props.config.proactiveOnSpecificCountryRegionCountry || '',
                    isDisplayable: true
                },
                ProactiveType: {
                    value: ProactiveChatNames.SpecificMarket,
                    isDisplayable: true
                }
            };
        };
    }

    /**
     * Generates chat welcome message to be used if proactive chat is triggered.
     * @returns {string} Chat welcome message.
     */
    public getMessage(): string {
        return this.props.config.proactiveOnSpecificCountryRegionMessage || this.props.config.proactiveDefaultMessage;
    }
}
